/**
 * Atlas component library — formal React wrappers around the design
 * tokens defined in `index.css` (surfaces / rules / brass treatment).
 *
 * The tokens already give us a single source of truth for *values*
 * (colours, gradients, shadows). These components add a single source
 * of truth for *patterns* — the JSX that authors write to use those
 * values. Before this file every Cartouche-shaped chip in the chronicle
 * maps was a hand-written `<div className="rounded-sm px-3 py-2"
 * style={{ background: "var(--surface-cartouche)", border: "1px solid
 * hsl(43, 50%, 35% / 0.3)" }}>` — same JSX, repeated. After: a single
 * `<Cartouche>` that other surfaces won't drift away from.
 *
 * Each component:
 *   - Accepts `className` to allow per-call customisation without
 *     requiring escape hatches.
 *   - Forwards refs (where it makes sense) so Radix / Framer wrappers
 *     can target the underlying DOM node.
 *   - Stays presentational only — no state, no effects, no data fetching.
 */
import { forwardRef, ReactNode, HTMLAttributes, PointerEvent, useCallback, useRef, useState } from "react";
import { motion, HTMLMotionProps } from "framer-motion";
import { cn } from "@/lib/utils";

/* ──────────────────────────────────────────────────────────────────────
   Cartouche
   ────────────────────────────────────────────────────────────────────── */

interface CartoucheProps extends HTMLAttributes<HTMLDivElement> {
  /** Children rendered inside the cartouche. */
  children: ReactNode;
  /**
   * `default` — recessed inner card on parchment / atlas-tile contexts.
   *             Brass-dim border, no shadow. Used for legend chips,
   *             city-marker pills, etc.
   * `elevated` — same surface but with a stronger border + drop shadow,
   *              for callouts that should "pop" off the surface.
   */
  variant?: "default" | "elevated";
}

/**
 * Cartouche — recessed cartography chip. Uses `--surface-cartouche` and
 * brass border colours. Perfect for inline legend chips, status pills,
 * city-name markers, and any "this is a captioned label on a chart" UI.
 */
export const Cartouche = forwardRef<HTMLDivElement, CartoucheProps>(
  ({ children, variant = "default", className, ...rest }, ref) => {
    const borderAlpha = variant === "elevated" ? 0.45 : 0.32;
    const shadow =
      variant === "elevated"
        ? "0 2px 8px -2px hsl(0 0% 0% / 0.4), inset 0 1px 0 hsl(43, 50%, 50% / 0.06)"
        : "inset 0 1px 0 hsl(43, 50%, 50% / 0.04)";
    return (
      <div
        ref={ref}
        className={cn("atlas-cartouche rounded-sm", className)}
        style={{
          border: `1px solid hsl(43, 50%, 35% / ${borderAlpha})`,
          boxShadow: shadow,
        }}
        {...rest}
      >
        {children}
      </div>
    );
  }
);
Cartouche.displayName = "Cartouche";

/* ──────────────────────────────────────────────────────────────────────
   GoldRule
   ────────────────────────────────────────────────────────────────────── */

interface GoldRuleProps extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  /**
   * Visual emphasis of the rule. Maps to the `--rule-gold-*` token
   * family — keep the names symmetric with the tokens so authors can
   * predict which one they want without checking the file.
   */
  intensity?: "strong" | "default" | "soft" | "faint";
  /** Brass-toned variant — drops the gold accent for a darker rule
   *  better suited to areas where gold would compete with content. */
  tone?: "gold" | "brass";
  /** Hairline (1px) or feature divider (2px). Defaults to hairline. */
  weight?: "thin" | "thick";
}

/**
 * GoldRule — horizontal divider strip. Wraps the `--rule-gold-*` and
 * `--rule-brass-*` tokens; pass through props go to the underlying div
 * so it can carry margins, widths, etc. via className or style.
 */
export const GoldRule = forwardRef<HTMLDivElement, GoldRuleProps>(
  ({ intensity = "default", tone = "gold", weight = "thin", className, style, ...rest }, ref) => {
    const heightClass = weight === "thick" ? "h-[2px]" : "h-px";
    let varName: string;
    if (tone === "brass") {
      varName = intensity === "faint" || intensity === "soft" ? "--rule-brass-dim" : "--rule-brass";
    } else {
      varName =
        intensity === "strong" ? "--rule-gold-strong"
        : intensity === "soft" ? "--rule-gold-soft"
        : intensity === "faint" ? "--rule-gold-faint"
        : "--rule-gold";
    }
    return (
      <div
        ref={ref}
        className={cn(heightClass, className)}
        style={{ background: `var(${varName})`, ...style }}
        aria-hidden="true"
        {...rest}
      />
    );
  }
);
GoldRule.displayName = "GoldRule";

/* ──────────────────────────────────────────────────────────────────────
   BrassButton
   ────────────────────────────────────────────────────────────────────── */

type BrassButtonOwnProps = {
  /** Optional leading icon. Sized 14px to match the default text scale. */
  icon?: ReactNode;
  /** Children — the visible label. Hidden under lg: when `responsive` is
   *  true (default), so the button collapses to icon-only on mobile and
   *  tablet — labels only appear once there's enough horizontal room
   *  for them at desktop widths (≥1024 px). */
  children?: ReactNode;
  /** True when the button represents an active toggled state — primary
   *  tint instead of muted. */
  active?: boolean;
  /**
   * `nav` (default) — the standard nav-brass-btn engraving used in
   *                    home-page and play-screen headers. Min 44×44 on
   *                    touch, denser on sm+.
   * `compact` — square 32px / 36px button without label, for icon
   *             clusters next to nav (music, settings).
   */
  size?: "nav" | "compact";
  /** When `nav` size, hide the label under lg: so the button collapses
   *  to its icon on mobile + tablet. Defaults to true (matches existing
   *  nav). */
  responsive?: boolean;
  /** Skip the framer-motion press/hover springs. Defaults to false. */
  staticMotion?: boolean;
};

type BrassButtonProps = BrassButtonOwnProps &
  Omit<HTMLMotionProps<"button">, keyof BrassButtonOwnProps>;

/**
 * BrassButton — engraved nav-style button, the workhorse of the home
 * and play headers. Wraps the `.nav-brass-btn` class plus the
 * border/text colour treatment that the home-page nav cluster has been
 * authoring inline since the original design.
 *
 * Active state lights the button with the primary tint; non-active
 * uses the muted-foreground / hover-foreground pair. Hover/press
 * springs come from framer-motion (set staticMotion to disable).
 */
export const BrassButton = forwardRef<HTMLButtonElement, BrassButtonProps>(
  (
    {
      icon,
      children,
      active = false,
      size = "nav",
      responsive = true,
      staticMotion = false,
      className,
      type = "button",
      title,
      "aria-label": ariaLabel,
      onPointerDown,
      onPointerUp,
      onPointerCancel,
      onPointerLeave,
      ...rest
    },
    ref
  ) => {
    const [touchLabelVisible, setTouchLabelVisible] = useState(false);
    const touchLabelTimerRef = useRef<number | null>(null);

    const clearTouchLabelTimer = useCallback(() => {
      if (touchLabelTimerRef.current !== null) {
        window.clearTimeout(touchLabelTimerRef.current);
        touchLabelTimerRef.current = null;
      }
    }, []);

    const hideTouchLabel = useCallback(() => {
      clearTouchLabelTimer();
      setTouchLabelVisible(false);
    }, [clearTouchLabelTimer]);

    const scheduleTouchLabel = useCallback(() => {
      clearTouchLabelTimer();
      touchLabelTimerRef.current = window.setTimeout(() => {
        setTouchLabelVisible(true);
        touchLabelTimerRef.current = null;
      }, 360);
    }, [clearTouchLabelTimer]);

    const sizeClasses =
      size === "compact"
        ? "flex items-center justify-center min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 sm:w-8 sm:h-8 rounded-sm"
        // Nav buttons are icon-only until lg: (≥ 1024 px) when labels
        // are revealed. On tablets (sm-md) we keep the icon-only state
        // but bump the visible height to ~36 px so it reads as a real
        // chip rather than a tiny stub — and tighten padding so the
        // five-button row fits the tablet header without colliding
        // with the logo on the left.
        : "nav-brass-btn flex items-center gap-2 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-[36px] lg:min-h-0 justify-center sm:px-2.5 lg:px-4 sm:py-1.5 text-xs font-display uppercase tracking-[0.15em] border rounded-sm";
    const stateClasses = active
      ? "bg-primary/20 border-primary text-primary"
      : "border-border text-muted-foreground hover:text-foreground hover:border-primary/30";

    const motionProps = staticMotion
      ? {}
      : { whileHover: { scale: 1.05 }, whileTap: { scale: 0.95 } };

    // Auto-derive title + aria-label from a plain-string label so the
    // collapsed icon-only state (mobile + tablet, below lg:) still
    // surfaces the button's name on hover, on iOS long-press, and to
    // screen readers — without each call site having to repeat the
    // label as a title prop.
    // Find the first plain-string descendant — supports children that are
    // a bare string ("Timeline") OR an array that mixes a label with
    // adornments like a notification badge ("Achievements" + <span/>).
    const extractLabel = (node: ReactNode): string | undefined => {
      if (typeof node === "string") return node;
      if (Array.isArray(node)) {
        for (const child of node) {
          const found = extractLabel(child as ReactNode);
          if (found) return found;
        }
      }
      return undefined;
    };
    const labelText = extractLabel(children);
    const derivedTitle = title ?? labelText;
    const derivedAriaLabel = ariaLabel ?? labelText;

    const handlePointerDown = useCallback(
      (event: PointerEvent<HTMLButtonElement>) => {
        onPointerDown?.(event);
        if (event.pointerType === "touch" || event.pointerType === "pen") {
          scheduleTouchLabel();
        }
      },
      [onPointerDown, scheduleTouchLabel]
    );

    const handlePointerUp = useCallback(
      (event: PointerEvent<HTMLButtonElement>) => {
        onPointerUp?.(event);
        hideTouchLabel();
      },
      [hideTouchLabel, onPointerUp]
    );

    const handlePointerCancel = useCallback(
      (event: PointerEvent<HTMLButtonElement>) => {
        onPointerCancel?.(event);
        hideTouchLabel();
      },
      [hideTouchLabel, onPointerCancel]
    );

    const handlePointerLeave = useCallback(
      (event: PointerEvent<HTMLButtonElement>) => {
        onPointerLeave?.(event);
        hideTouchLabel();
      },
      [hideTouchLabel, onPointerLeave]
    );

    return (
      <motion.button
        ref={ref}
        type={type}
        className={cn(
          sizeClasses,
          // Compact buttons re-use the same active/inactive border treatment
          // even though they aren't engraved — keeps the visual grammar
          // consistent across the cluster.
          size === "compact"
            ? active
              ? "bg-primary/10 border border-primary/30 text-primary"
              : "border border-border text-muted-foreground hover:text-foreground hover:border-primary/30"
            : stateClasses,
          "group relative transition-all duration-300",
          className
        )}
        {...motionProps}
        {...rest}
        title={derivedTitle}
        aria-label={derivedAriaLabel}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerCancel}
        onPointerLeave={handlePointerLeave}
      >
        {icon && <span className="flex-shrink-0">{icon}</span>}
        {children && size === "nav" && (
          <span className={responsive ? "hidden lg:inline" : ""}>{children}</span>
        )}
        {derivedTitle && responsive && (
          <span
            className={cn(
              "pointer-events-none absolute left-1/2 top-full z-50 mt-2 -translate-x-1/2 whitespace-nowrap rounded-sm border border-border bg-popover px-2 py-1 font-display text-[11px] uppercase tracking-[0.14em] text-popover-foreground shadow-lg transition-opacity duration-150 lg:hidden",
              // Not shown: display none, so it takes no room past the screen edge.
              touchLabelVisible ? "opacity-100" : "hidden max-lg:group-hover:block max-lg:group-focus-visible:block group-hover:opacity-100"
            )}
          >
            {derivedTitle}
          </span>
        )}
      </motion.button>
    );
  }
);
BrassButton.displayName = "BrassButton";
